import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Optional, Output, QueryList, TemplateRef, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IonIcon, IonSkeletonText } from '@ionic/angular/standalone';
import { BehaviorSubject, Observable, ReplaySubject, Subject, combineLatest, lastValueFrom, of } from 'rxjs';
import {
  catchError,
  delay,
  map,
  startWith,
  switchMap,
  take,
  throttleTime
} from 'rxjs/operators';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { IThingie } from 'src/app/model/thingie/thingie';
import { ICard, IVisibilityState, IVisibilitySubject } from 'src/app/model/thingie/thingie-card';
import { GraphConfigSeriesDataLoaderService } from 'src/app/services/data/series-data-loader/graph-config-series-data-loader.service';
import { PlotlyGeneratorProviderService } from 'src/app/services/plotly-generator/plotly-generator-provider.service';
import { TabStorageService } from 'src/app/services/tab-storage/tab-storage.service';
import { ThingieService } from 'src/app/services/thingie/thingie-service.service';
import { UserService } from 'src/app/services/user/user.service';
import { ThingieNamePipe } from 'src/app/utility/pipes/thingie-name';
import { ThingieStatusHelper } from 'src/app/utility/thingie-status-helper';
import { defaultThingieComparator } from 'src/app/utility/thingie/default-thingie-comparator';
import { initializeCards } from 'src/app/utility/thingie/thingie-card-generator';
import { isNotUndefined } from 'src/app/utility/types';
import { DefaultUrls } from 'src/assets/default.urls';
import { SubSink } from 'subsink';
import { PureFunctionCallPipe } from '../../../utility/pipes/pure-member-call/pure-member-call.pipe';
import { DialogControl } from '../../dialogs/dialog/dialog-control';
import { StringEllipsisComponent } from '../../string-ellipsis/string-ellipsis.component';
import { ThingieStateChipComponent } from '../../thingie-state-chip/thingie-state-chip.component';
import { CardPlotlyComponent } from '../card-plotly/card-plotly.component';

@Component({
  selector: 'app-thingie-card-list',
  templateUrl: './thingie-card-list.component.html',
  styleUrls: [
    '../thingie-card-view/thingie-card-view.component.scss'
  ],
  imports: [ MaterialImportsModule, ThingieNamePipe, IonSkeletonText, RouterLink, NgClass, NgTemplateOutlet, StringEllipsisComponent, IonIcon, ThingieStateChipComponent, CardPlotlyComponent, AsyncPipe, PureFunctionCallPipe,]
})
export class ThingieCardListComponent implements AfterViewInit, OnDestroy {
  protected readonly urls = DefaultUrls;

  @Input() public set selectedCards(cards: ICard[]) {
    this.selectedCards$.next(cards);
  }

  public get selectedCards() {
    return this.selectedCards$.value;
  }

  protected selectedCards$ = new BehaviorSubject<ICard[]>([]);

  @Input()
  public thingies!: IThingie[];

  @Input()
  public set cards(newCards: ICard[]) {
    this._cards = newCards;
    if (this._resetCardsOnNextInputUpdate === true) {
      this.resetVisibilityStatesMap();
      this.visibilityStatesMap.clear();

      this._resetCardsOnNextInputUpdate = false;
    }
  }

  public get cards() {
    return this._cards;
  }

  private _cards!: ICard[];

  @Output()
  public selectedStackCard = new EventEmitter<ICard>();

  // visibility tracking
  @ViewChildren('thingieCards')
  private plotlyCards!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren(CardPlotlyComponent)
  private plotlyCardComponents!: QueryList<CardPlotlyComponent>;

  private visibilityObserver: IntersectionObserver | undefined;
  private visibilityStatesMap =
    new Map<string, IVisibilityState>();

  private stackPopupThingieId$ = new BehaviorSubject<string | null>(null);
  protected stackQueryParams: { [queryParam: string]: string | number } = {};
  private cardContent!: TemplateRef<any>;
  @Input({ required: true })
  public idPrefix!: string;
  private defaultPopupMode: 'stacked' | 'single' = 'stacked';

  private subscriptions = new SubSink();
  private stackPopupTemplate$ = new ReplaySubject<TemplateRef<any>>(1);

  private _resetCardsOnNextInputUpdate = false;

  constructor(
    activatedRoute: ActivatedRoute,
    private thingieService: ThingieService,
    private userService: UserService,
    private router: Router,
    private generator: PlotlyGeneratorProviderService,
    private dataloader: GraphConfigSeriesDataLoaderService,
    private tabStorage: TabStorageService,
    @Optional() private dialogControl?: DialogControl
  ) {
    // Keep track of query parameters to keep the dashboard sub-section
    // tab selection alive when clicking on a card stack.
    // This is done by attaching the query parameters
    // to the routerLink of card stacks.
    this.subscriptions.sink = activatedRoute.queryParams.subscribe(
      (params) => this.stackQueryParams = params
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.visibilityObserver?.disconnect();
    this.visibilityStatesMap.forEach((visibleCardState) => {
      visibleCardState.subject$.complete();
    });
  }

  public async ngAfterViewInit() {
    // whenever the list of plotwrappers changes, try to plot the next plot.
    this.subscriptions.sink = this.plotlyCards.changes
      .pipe(
        // also try to plot once at the beginning
        startWith(undefined),
        // plot on another microtask, to combat NG0100 errors
        delay(0)
      )
      .subscribe(() => {
        this.plotNextPlot();
      });

    this.setupLoadPlotData();

    // using single observer to control the changes
    this.visibilityObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach(entry => {
          const elementId = entry.target.id;
          // fetch from shownCards, that is used in UI

          const card = this.selectedCards.find(c => c.thingieIds[0] === elementId);
          if (card !== undefined) {
            if (this.visibilityStatesMap.has(elementId) === false) {
              const visibilityState: IVisibilityState = {
                initialized: false,
                card,
                hide: undefined,
                isVisible: entry.isIntersecting,
                subject$: new Subject(),
              };

              // check the visibility change after 1s if it is stable
              this.subscriptions.sink = visibilityState.subject$.pipe(
                throttleTime(1000, undefined, { leading: true, trailing: true }),
              ).subscribe((val) => {
                this.updateVisibilityStates(val.id, val.isIntersecting, val.card);
              });

              this.visibilityStatesMap.set(elementId, visibilityState);
            }

            const subject: IVisibilitySubject = { id: elementId, isIntersecting: entry.isIntersecting, card };
            this.visibilityStatesMap.get(elementId)?.subject$.next(subject);
          }
        });
      },
      {
        rootMargin: '1500px 0px 1500px 0px',
        threshold: 0,
        root: null
      }
    );

    // whenever the list is changed, we can observer the cards to load the data
    this.subscriptions.sink = this.plotlyCards.changes.subscribe(() => {
      if (this.visibilityObserver !== undefined) {
        this.visibilityObserver.disconnect();
        // refresh the map and unobserve them
        this.resetVisibilityStatesMap();

        this.plotlyCards.forEach((element) => {
          if (this.visibilityObserver !== undefined) {
            this.visibilityObserver.observe(element.nativeElement);
          }
        });
      }
    });
  }

  private resetVisibilityStatesMap() {
    this.visibilityStatesMap.forEach((visibleCardState) => {
      visibleCardState.hide?.unsubscribe();
      visibleCardState.hide = undefined;
      visibleCardState.subject$.complete();
      visibleCardState.isVisible = false; // make sure the the "hide" won't run after setting new observer
    });
  }

  // update the visibility states of cards
  private updateVisibilityStates(id: string, isIntersecting: boolean, card: ICard) {
    const cardVisibility = this.visibilityStatesMap.get(id);

    if (cardVisibility !== undefined) {
      if (cardVisibility.isVisible === true && isIntersecting === false) {
        if (cardVisibility.hide === undefined) {
          cardVisibility.hide = of([]).pipe(
            delay(2000)
          )
            .subscribe(() => {
              if (cardVisibility !== undefined) {
                cardVisibility.isVisible = false;
              }
            });
        }
      } else if (isIntersecting === true) {
        if (cardVisibility.hide !== undefined) {
          cardVisibility.hide?.unsubscribe();
          cardVisibility.hide = undefined;
        }

        if (cardVisibility.initialized === false) {
          cardVisibility.initialized = true;

          initializeCards([card], this.tabStorage, this.dataloader, this.generator, this.subscriptions);
        }

        cardVisibility.isVisible = true;
      }

      // update the map
      this.visibilityStatesMap.set(id, cardVisibility);
    }
  }

  /**
   * Look for the next plot that is not plotting yet, as indicated by the
   * `data-plot` property being not 'true', and set the `data-plot` attribute
   * to 'true' so that it will get plotted.
   */
  protected plotNextPlot() {
    const hasPlottingGraphs = this.plotlyCardComponents
      .filter(card => card.readyToPlot === false && card.plotting === true && card.plotComplete !== true)
      .length > 0;
    if (hasPlottingGraphs) {
      return;
    }
    for (const card of this.plotlyCardComponents) {
      if (card.readyToPlot === true && card.plotting !== true) {
        card.plotting = true;
        return;
      }
    }
  }

  private setupLoadPlotData() {
    this.subscriptions.sink = this.selectedCards$.pipe(switchMap(() => {
      const visibleCards = Array.from(this.visibilityStatesMap.values()).filter(v => v.isVisible === true)
        .map(v => v.card);

      const runningInfo = combineLatest(visibleCards.map(card =>
        combineLatest(card.thingieIds.map(id => this.thingieService.getThingieById(id).pipe(take(1), catchError(() => of(undefined)))))
          .pipe(
            map(thingies => thingies.filter(isNotUndefined)),
            map(thingies => thingies.flatMap(thingie => thingie.processes.flatMap(process => process.tasks.map(task =>
              ThingieStatusHelper.getTasksStatus([task]))))),
            map(statusInfos => ({ card, isRunning: statusInfos.some(statusInfo => statusInfo.hasRunning) }))
          )));

      // load all data once (if layout is not generated yet) and then only if thingie is running
      return runningInfo.pipe(
        map(runningInfos => runningInfos
          .filter(runningInfo => runningInfo.card.previewTab.layout === undefined || runningInfo.isRunning)
          .map(runningInfo => runningInfo.card)),
        map(cards =>
          initializeCards(cards, this.tabStorage, this.dataloader, this.generator, this.subscriptions))
      );
    }))
      .subscribe();
  }

  protected routeToReplicate(url: string) {
    void this.router.navigate([url]);
    void this.dialogControl?.requestCloseDialog('noNavigation');
  }

  protected getBaseIdForCard(idx: number, card: ICard) {
    return card.thingieIds[0];
  }

  protected getThingiesByIds = (ids: string[]): Observable<IThingie[]> => {
    if (ids.length === 0) {
      return of([]);
    }
    return combineLatest(ids.map(id => this.thingieService.getThingieById(id))).pipe(
      startWith([])
    );
  };


  protected async removePinnedCard(thingieId: string, card: ICard) {
    const relatives: string[] = [];

    (await lastValueFrom(this.thingieService.getThingieById(thingieId).pipe(take(1))
      .pipe(map(thingie => {
        thingie.relations.forEach(relation => {
          if (relation.type === 'experimentGroup') {
            relatives.push(relation.relatedThingie);
          }
        });
      }))))!;

    relatives.push(thingieId);

    this.userService.unpinThingies(relatives);

    const thingieString: string = relatives.join(':');

    void this.userService.removePinnedCard(thingieString);

    card.isPinned = false;
  }

  protected async addPinnedCard(thingieId: string, card: ICard) {
    const relatives: string[] = [];

    (await lastValueFrom(this.thingieService.getThingieById(thingieId).pipe(take(1))
      .pipe(map(thingie => {
        thingie.relations.forEach(relation => {
          if (relation.type === 'experimentGroup') {
            relatives.push(relation.relatedThingie);
          }
        });
      }))))!;

    relatives.push(thingieId);

    this.userService.pinThingies(relatives);

    const thingieString: string = relatives.join(':');

    void this.userService.addPinnedCard(thingieString);

    card.isPinned = true;
  }

  protected _thingieLinkClicked(card: ICard) {
    this.selectedStackCard.emit(card);
  }

  public resetCardsOnNextUpdate() {
    this._resetCardsOnNextInputUpdate = true;
  }

  /**
   * Utility function to sort a list of thingies within the template in a pipe chain
   *
   * @param list source list of thingies
   * @returns thingies sorted by name
   */
  protected sortThingiesByName(list: IThingie[]): IThingie[] {
    return list.sort(defaultThingieComparator);
  }
}
